/** Plan/week aggregation helpers (pure, unit-tested). */
import { addDays, format, parseISO, startOfISOWeek } from "date-fns";
import type { PlannedSession, Week } from "@/api/schemas";

export function mondayOf(d: string | Date): string {
  const date = typeof d === "string" ? parseISO(d) : d;
  return format(startOfISOWeek(date), "yyyy-MM-dd");
}

export interface PlanWeek {
  week_start: string;
  days: { day: string; sessions: PlannedSession[] }[];
  phase: string | null;
  planned_hours: number;
  planned_load: number;
  has_b2b: boolean;
  has_long: boolean;
  is_recovery: boolean;
  actual: Week | null;
}

/** Group sessions into Monday-based weeks; join actual per-week data when present. */
export function groupPlanByWeek(
  sessions: PlannedSession[],
  weeks: Week[] = [],
  range?: { from: string; to: string },
): PlanWeek[] {
  const byWeek = new Map<string, PlannedSession[]>();
  for (const s of sessions) {
    const k = mondayOf(s.day);
    const arr = byWeek.get(k);
    if (arr) arr.push(s);
    else byWeek.set(k, [s]);
  }
  const actual = new Map(weeks.map((w) => [w.week_start, w]));
  let starts = [...new Set([...byWeek.keys(), ...(range ? [] : actual.keys())])];
  if (range) {
    starts = [];
    for (let d = parseISO(mondayOf(range.from)); d <= parseISO(range.to); d = addDays(d, 7)) {
      starts.push(format(d, "yyyy-MM-dd"));
    }
  }
  starts.sort();
  return starts.map((ws) => {
    const ss = (byWeek.get(ws) ?? []).slice().sort((a, b) => a.day.localeCompare(b.day) || a.id - b.id);
    const days = Array.from({ length: 7 }, (_, i) => {
      const d = format(addDays(parseISO(ws), i), "yyyy-MM-dd");
      return { day: d, sessions: ss.filter((s) => s.day === d) };
    });
    const act = actual.get(ws) ?? null;
    const active = ss.filter((s) => s.status !== "skipped");
    const planned_hours = round1(active.reduce((a, s) => a + s.target_hours, 0));
    return {
      week_start: ws,
      days,
      phase: ss[0]?.phase ?? act?.phase ?? null,
      planned_hours,
      planned_load: Math.round(active.reduce((a, s) => a + s.target_load, 0)),
      has_b2b: ss.some((s) => s.kind === "back_to_back"),
      has_long: ss.some((s) => s.kind === "long"),
      is_recovery: act?.is_recovery ?? ss.some((s) => /recovery week/i.test(s.description)),
      actual: act,
    };
  });
}

export function round1(x: number): number {
  return Math.round(x * 10) / 10;
}

/** Share of low/mid/high intensity hours in a week (percent, rounded). */
export function intensitySplit(w: Pick<Week, "z_low_h" | "z_mid_h" | "z_high_h">): {
  low: number;
  mid: number;
  high: number;
} | null {
  const t = w.z_low_h + w.z_mid_h + w.z_high_h;
  if (t <= 0) return null;
  const low = Math.round((w.z_low_h / t) * 100);
  const high = Math.round((w.z_high_h / t) * 100);
  return { low, mid: 100 - low - high, high };
}

/** Phases get deeper Majorelle blue as the race gets closer; the summer season is ochre (gravel).
 * CSS variables with dark variants (styles.css `--ph-*`). */
export const PHASE_COLORS: Record<string, string> = {
  "Base 1": "var(--ph-base1)",
  Base: "var(--ph-base1)",
  "Base 2": "var(--ph-base2)",
  "Build 1": "var(--ph-build1)",
  "Build 2": "var(--ph-build2)",
  "Season specific": "var(--ph-season)",
  Transition: "var(--ph-transition)",
  Peak: "var(--ph-peak)",
  Taper: "var(--ph-taper)",
};
export const phaseColor = (name: string | null | undefined) =>
  (name && PHASE_COLORS[name]) || "var(--ph-none)";

/** Session kinds → the colour family of the Plan chips' left stripe (styles.css `--k-*`). */
export function sessionTone(
  kind: string,
): "endurance" | "long" | "intensity" | "strength" | "rest" | "other" {
  switch (kind) {
    case "rest":
      return "rest";
    case "long":
    case "back_to_back":
      return "long";
    case "endurance":
    case "recovery":
      return "endurance";
    case "tempo_climbs":
    case "sweet_spot":
    case "ftp_test":
    case "race":
      return "intensity";
    case "strength":
      return "strength";
    default:
      return "other"; // hockey, other sports, custom
  }
}
